import React from 'react';
import { useTranslation } from 'react-i18next';
import { Draggable } from "react-beautiful-dnd";
import { CopyButton, Icon, UserAvatar, ChangeTrack, Tooltip } from '../../../components';
import ClientReplyPendingBadge from '../../ClientReplyPendingBadge/ClientReplyPendingBadge';
import { Change, Commit, ProjectLabel } from '../../../models';
import { useAppSelector } from '../../../redux';
import { Badge, Progress } from 'flowbite-react';
import moment from 'moment';
import { formatMomentDate } from '../../../locales';

interface CardProps {
    change: Change;
    isSmart?: boolean;
    expanded?: boolean;
    index: number;
    onEdit: (change: Change) => void;
    onLabelClick?: (labelId: string) => void;
    hideLabelNames?: boolean;
    showPinAction?: boolean;
    isPinned?: boolean;
    onTogglePin?: (change: Change) => void;
    onOpenInNewTab?: (change: Change) => void;
}

const Card = (props: CardProps) => {
    const { t, i18n } = useTranslation();
    const {
        change, isSmart, expanded, index, onEdit, onLabelClick, hideLabelNames,
        showPinAction, isPinned, onTogglePin, onOpenInNewTab,
    } = props;

    const progression = (typeof change?.progression) === 'function' ?
        Math.round(change?.progression()) : 0;

    const { user }   = useAppSelector(state => state.auth);
    const { active } = useAppSelector(state => state.tracks);
    const { project } = useAppSelector(state => state.projects);

    const currentChange = useAppSelector(state => state.changes.change);

    const content = React.useMemo(() => {
        if (!change?._id)
            return null;

        const ch : Change = currentChange?._id === change._id ? currentChange : change;
        if (!ch)
            return null;

        return (
            <>
                <div className="border-b border-gray-100 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 pl-2 pr-2.5 py-2 flex justify-between items-center gap-2 rounded-t-lg min-w-0">
                    <div className="flex space-x-2 min-w-0 overflow-hidden items-center">
                        <Tooltip content={t("changes.badge.slug")}>
                            <div>
                                <Badge color={ch.color()} className="w-fit text-xs px-1">
                                    <div className="flex">
                                        <Icon type={ch.iconName()} size={3} color="current" className="inline" />&nbsp;
                                        <CopyButton title={ ch.slugName() } textToCopy={ ch.slugName() } color="none" hover />
                                    </div>
                                </Badge>
                            </div>
                        </Tooltip>
                        {(ch.nbComments > 0) && (
                            <Tooltip content={t("changes.badge.comment")}>
                                <div>
                                    <Badge color="indigo" className="w-fit text-xs px-1">
                                        <Icon type="comment" color="current" className="inline" />&nbsp;
                                        {ch.nbComments}
                                    </Badge>
                                </div>
                            </Tooltip>
                        )}
                        {ch.issue?.id && (
                            <Tooltip content={t(`changes.badge.issue_triggered_by_${ch.issue.source}`)}>
                                <div>
                                    <Badge color="failure" className="w-fit text-xs px-1">
                                        <Icon type={ ch.issueIconName() } color="current" className="inline" />&nbsp;
                                    </Badge>
                                </div>
                            </Tooltip>
                        )}
                        {ch.nbAttachments > 0 && (
                            <Tooltip content={t("changes.badge.attachment")}>
                                <div>
                                    <Badge color="gray" className="w-fit text-xs px-1">
                                        <Icon type="file" color="current" className="inline" />&nbsp;
                                        {ch.nbAttachments}
                                    </Badge>
                                </div>
                            </Tooltip>
                        )}
                        {ch.hasTechnicalContext() && (
                            <Tooltip content={t("changes.badge.technical_context")}>
                                <div>
                                    <Badge color="indigo" className="w-fit text-xs px-1">
                                        <Icon type="notes" color="current" className="inline" />
                                    </Badge>
                                </div>
                            </Tooltip>
                        )}
                        <ClientReplyPendingBadge change={ch} dismissable />
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                        {showPinAction && onTogglePin && (
                            <Tooltip content={isPinned ? (t('changes.unpin') || '') : (t('changes.pin') || '')}>
                                <button
                                    type="button"
                                    className={`inline-flex h-6 w-6 items-center justify-center rounded ${
                                        isPinned
                                            ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 hover:bg-amber-200'
                                            : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-amber-700'
                                    }`}
                                    aria-label={isPinned ? (t('changes.unpin') || '') : (t('changes.pin') || '')}
                                    aria-pressed={!!isPinned}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onTogglePin(ch);
                                    }}
                                >
                                    <Icon type="pin" size={3} color="current" />
                                </button>
                            </Tooltip>
                        )}
                        {ch.assignee && !ch.isTimeTrackable(user) && (
                            <Tooltip content={ch.assignee?.firstname + ' ' + ch.assignee?.lastname}>
                                <div className="shrink-0">
                                    <UserAvatar size={6} user={ch.assignee} noTooltip />
                                </div>
                            </Tooltip>
                        )}
                        {ch.assignee && ch.isTimeTrackable(user) && (
                            <div className="group/card-track shrink-0 self-center h-6 w-6 flex justify-center items-center">
                                {active?.change?._id === ch._id ? (
                                    <ChangeTrack change={ch} />
                                ) : (
                                    <>
                                        <UserAvatar size={6} user={ch.assignee} noTooltip className="group-hover/card-track:hidden" />
                                        <ChangeTrack className="hidden group-hover/card-track:block" change={ch} />
                                    </>
                                )}
                            </div>
                        )}
                        {!ch.assignee && !showPinAction && (
                            <div className="w-fit h-[24px] shrink-0"></div>
                        )}
                    </div>
                </div>
                {progression > 0 && (
                    <div className="p-1 h-4">
                        <Tooltip content={progression + '%'}>
                            <div>
                                <Progress
                                    progress={progression}
                                    size="sm"
                                    color={ progression === 100 ? 'green' : (progression > 20 ? 'cyan' : 'yellow')}
                                />
                            </div>
                        </Tooltip>
                    </div>
                )}
                <h4 className="text-sm font-normal heading-md my-1 px-2">
                    {ch.title[i18n.language]}
                </h4>
                {((project?.labels || []).length > 0 && (ch.labelIds || []).length > 0) && (
                    <div className={`flex flex-wrap items-center px-2 pb-1 ${hideLabelNames ? 'gap-0.5' : 'gap-1'}`}>
                        {(project.labels || [])
                            .filter((label: AnyObj) => (ch.labelIds || []).includes(label._id))
                            .map((label: AnyObj) => (
                                    <button
                                        key={label._id}
                                        type="button"
                                        className={hideLabelNames
                                            ? 'h-2 w-10 shrink-0 rounded-sm'
                                            : 'inline-flex max-w-full items-center gap-0.5 truncate rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight'}
                                        style={{
                                            backgroundColor: label.color,
                                            color: hideLabelNames ? undefined : ProjectLabel.textColor(label.color),
                                        }}
                                        title={label.name}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onLabelClick?.(label._id);
                                        }}
                                    >
                                        {!hideLabelNames && (
                                            <>
                                                <Icon type="tag" size={3} className="shrink-0" color="inherit" />
                                                <span className="truncate">{label.name}</span>
                                            </>
                                        )}
                                    </button>
                                ))}
                    </div>
                )}
                <div className="flex justify-between text-sm font-normal p-2 border-t">
                    <Tooltip content={t("changes.badge.priority")}>
                        <div>
                            <Badge color="indigo" className="w-fit" size="xs">
                                <Icon type="priority" color="current" className="inline mr-1" />
                                {ch.priority}
                            </Badge>
                        </div>
                    </Tooltip>
                    {ch.quote && (
                        <Tooltip content={t("changes.badge.quote")}>
                            <div>
                                <Badge color="blue" className="w-fit text-xs px-1">
                                    <Icon type="law" color="current" className="inline" />&nbsp;
                                    {ch.quote.slug} | {t(`quotes.status_type.${ch.quote.status}`)}
                                </Badge>
                            </div>
                        </Tooltip>
                    )}
                </div>
                <div className="flex items-center justify-between gap-1 min-w-0 p-2 py-1 rounded-b-lg border-t overflow-hidden">
                    <div className="min-w-0 flex-1 overflow-hidden">
                        <Tooltip content={t("changes.badge.status")}>
                            <div className="min-w-0 max-w-full text-xs font-light text-gray-700 dark:text-gray-200 border border-gray-100 dark:border-gray-700 rounded px-2 flex flex-row">
                                <span className={`block whitespace-nowrap text-ellipsis overflow-hidden truncate ${ch.estimate > 0 ? 'max-w-[7rem]' : ''}`}>
                                    <Icon type="progress" size={3} color="current" className="inline mr-1"/>
                                    {t(ch.progressStatusLabelKey())}
                                </span>
                            </div>
                        </Tooltip>
                    </div>
                    {(ch.estimate > 0) && (
                        <Tooltip content={t("changes.badge.estimated_time")}>
                            <div className="shrink-0">
                                <Badge color="warning" className="w-fit whitespace-nowrap px-1">
                                    <>
                                        <Icon type="estimated" size={3} color="current" className="inline mr-1"/>
                                        {ch?.estimate} {t('changes.estimate_units_min')}
                                    </>
                                </Badge>
                            </div>
                        </Tooltip>
                    )}
                </div>
                {(ch.commits?.length > 0) && (
                    <Tooltip content={t("changes.badge.commit")}>
                        <div className="flex-col justify-center space-y-1 p-2 py-1 rounded-b-lg border-t">
                            {ch.commits?.map((commit: Commit) => (
                            <Badge key={commit.hash} color="success" className="w-fit px-1">
                                <Icon type="commit" size={3} color="current" className="inline"/>&nbsp;
                                {commit.message}
                            </Badge>
                            ))}
                        </div>
                    </Tooltip>
                )}
                {(ch.deadline || ch.sprints?.length > 0) && (
                    <div className="flex justify-between p-2 rounded-b-lg border-t">
                        {(ch.sprints?.length > 0) ? (
                            <Tooltip content={t("changes.badge.sprint")}>
                                <div>
                                    <Badge color="warning" size="xs" className="w-fit px-1">
                                        <span className="text-xs font-normal">
                                            <Icon type="sprint" color="current" className="inline mr-1.5"/>
                                            {ch.sprints.slice(-1)[0].name}
                                        </span>
                                    </Badge>
                                </div>
                            </Tooltip>
                        ) : (
                            <div></div>
                        )}
                        {ch.deadline && (
                            <Tooltip content={t("changes.badge.deadline")}>
                                <div>
                                    <Badge color={ch.deadline.isBefore(moment().startOf('day')) ? 'pink' : (ch.deadline.isAfter(moment()) ? 'green' : 'indigo')} size="xs" className="w-fit px-1">
                                        <span className="font-normal">
                                            <Icon type="date" color="current" className="inline mr-1.5"/>
                                            {formatMomentDate(ch.deadline, 'L')}
                                        </span>
                                    </Badge>
                                </div>
                            </Tooltip>
                        )}
                    </div>
                )}
                {ch.release && (
                    <Tooltip content={t("changes.badge.version")}>
                        <div className="flex items-center p-2 rounded-b-lg border-t">
                            <Badge color="indigo" size="sm" className="w-fit inline-flex items-center gap-1.5 px-1.5">
                                <Icon type="release" color="current" className="inline shrink-0"/>
                                {ch.release.version}
                            </Badge>
                        </div>
                    </Tooltip>
                )}
            </>
        );
    }, [
        (currentChange?._id && currentChange._id === change?._id && currentChange.updatedAt) || (active?.change?._id === change?._id),
        currentChange?._id === change?._id ? currentChange?.quote?._id : null,
        currentChange?._id === change?._id ? currentChange?.billable : null,
        change?.updatedAt,
        change?.quote?._id,
        change?.quote?.status,
        change?.billable,
        change?.labelIds?.join(','),
        project?.labels,
        i18n.language,
        onLabelClick,
        hideLabelNames,
        showPinAction,
        isPinned,
        onTogglePin,
    ]);

    const handleMiddleOpen = React.useCallback((event: React.MouseEvent) => {
        if (event.button !== 1 || !onOpenInNewTab)
            return;
        event.preventDefault();
        event.stopPropagation();
        onOpenInNewTab(change);
    }, [onOpenInNewTab, change]);

    if (!change?._id)
        return null;

    if (expanded || isSmart)
        return (
            <li
                className={`select-none mb-1 ${expanded ? 'col-span-1 h-full' : ''}`}
                onClick={() => onEdit(change)}
                onMouseDown={(event) => {
                    if (event.button === 1)
                        event.preventDefault();
                }}
                onAuxClick={handleMiddleOpen}
            >
                <div className={"h-full flex flex-col justify-between border border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 rounded-lg cursor-pointer bg-white text-black dark:bg-slate-700 dark:text-white min-h-max shadow-sm"}>
                    { content }
                </div>
            </li>
        );

    return (
        <Draggable draggableId={change?._id} index={index} >
            { (provided?: AnyObj, snapshot?: AnyObj) => (
                <>
                    <li
                        className={`select-none mb-1 ${expanded ? 'col-span-1 h-full' : ''}`}
                        {...(provided?.draggableProps || {})}
                        {...(provided?.dragHandleProps || {})}
                        ref={provided?.innerRef}
                        onClick={() => onEdit(change)}
                        onMouseDown={(event) => {
                            if (event.button === 1)
                                event.preventDefault();
                        }}
                        onAuxClick={handleMiddleOpen}
                    >
                        <div className={`${snapshot?.isDragging && !snapshot?.isDropAnimating ? 'rotate-[5deg]' : ''} h-full flex flex-col justify-between border border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 rounded-lg cursor-pointer bg-white text-black dark:bg-slate-700 dark:text-white min-h-max shadow-sm`}>
                            { content }
                        </div>
                    </li>
                    {provided?.placeholder}
                </>
            )}
        </Draggable>
    )
};

export default Card;
